import { TranslationsContext } from '@teliaee/telia-translate';
import { observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import {
  AnalyticsTrigger,
  IProductCardCartButtonHelper,
  IProductCardColor,
  IProductCardColorSelectOption,
  IProductCardColorSelectProps,
  IProductCardProps,
  ProductCard,
  TSelectSimpleValue
} from 'telia-front-react';
import Config from '../../Config';
import { AlternativeProduct, MonthlyPrice, Price, Product } from '../../content/ProductInterfaces';
import GaDataStore from '../../stores/GaDataStore';
import LanguageStore, { Language } from '../../stores/LanguageStore';
import { ESHOP_CATEGORY } from '../../stores/SearchStore';
import ShoppingCartStore from '../../stores/ShoppingCartStore';
import SsoStore, { CUSTOMER_TYPE_BUSINESS } from '../../stores/SsoStore';
import { FetchStatus } from '../../util/FetchStatus';
import { cloudinaryUrlWithSize, getImageSrcset } from '../../util/PathUtil';
import {
  FinanceType,
  getApplicablePrice,
  getFinanceTypeTranslation,
  priceValue,
  renderMonthlyPrice,
  resolvePriceEndDate,
  shouldCrossOutDiscountPrice
} from '../../util/PriceUtil';
import { addColon, cutLongName, localizeString } from '../../util/ProductUtil';
import { mapTags } from '../../util/TagUtil';
import { capitalize } from '../../util/TextUtil';
import { PRODUCT_STATUS_ARCHIVED } from '../results/EShopSearchResults';

interface ProductCardProps {
  product: Product;
  onClickCallback?: (val: any) => void;
  hasTelia1?: boolean;
  urlParam?: string;
  languageStore?: LanguageStore;
  ssoStore?: SsoStore;
  shoppingCartStore?: ShoppingCartStore;
}

const inactiveStatuses = ['archived', 'unavailable'];

@observer
class ProductCardWrapper extends React.Component<ProductCardProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  @observable accessor imageUri: string = this.props.product.pictures?.length && cloudinaryUrlWithSize('150', this.props.product.pictures[0], false);
  @observable accessor cartStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor selectedProductCode: string = null;

  constructor(props: ProductCardProps) {
    super(props);

    this.selectedProductCode = props.product.productCode;
  }

  componentDidUpdate(prevProps: Readonly<ProductCardProps>) {
    if (prevProps.product.productCode !== this.props.product.productCode) {
      this.imageUri = this.props.product.pictures?.length && cloudinaryUrlWithSize('150', this.props.product.pictures[0], false);
    }
  }

  private setToDefaultImage = () => {
    runInAction(() => {
      this.imageUri = `${Config.teliaShopUrl}/gfx/site-new/images/no-image/noimagext.png`;
    });
  };

  private setImageUri = (imageUri: string) => {
    runInAction(() => {
      if (imageUri) {
        this.imageUri = imageUri;
      }
    });
  };

  private handleColorSelect = (value: TSelectSimpleValue) => {
    runInAction(() => {
      this.selectedProductCode = value as string;
      this.setImageUri(this.props.product?.alternativeProducts?.find((altProduct: AlternativeProduct) => altProduct.productCode === value).picture);
    });
  };

  private handleAddToCart = async (event: React.MouseEvent) => {
    const { shoppingCartStore, product } = this.props;
    event.preventDefault();
    const productCode = this.selectedProductCode || product.productCode;
    const url = this.getSelectedProductUrl();
    GaDataStore.pushWithoutCmsData({ event: 'AutoEvent', eventCategory: 'Otsing', eventAction: 'Lisan ostukorvi', eventLabel: product.displayName?.et });
    runInAction(() => {
      this.cartStatus = FetchStatus.FETCHING;
    });

    try {
      const result = await shoppingCartStore.addProductToCart({ productCode, url, serviceProduct: product.serviceProduct });
      runInAction(() => {
        this.cartStatus = result ? FetchStatus.FETCHED : FetchStatus.ERROR;
      });
    } catch (_e) {
      runInAction(() => {
        this.cartStatus = FetchStatus.ERROR;
      });
    }
  };

  private getSelectedProductUrl = () => {
    const { product } = this.props;
    const productCode = this.selectedProductCode || product.productCode;
    const productUrl = this.props.product?.alternativeProducts?.find((altProduct: AlternativeProduct) => altProduct.productCode === productCode).url;
    return `${Config.teliaShopUrl}${productUrl}`;
  };

  private getBaseProps(): IProductCardProps & { key: string } {
    const translate = this.context.translate;
    const {
      product,
      urlParam,
      onClickCallback,
      languageStore: { currentLanguage },
      ssoStore
    } = this.props;

    const colorOptions = resolveColorSelectItems(product, currentLanguage);
    const colors: IProductCardColorSelectProps = colorOptions.length > 1 && {
      options: colorOptions,
      value: this.selectedProductCode,
      onChange: this.handleColorSelect,
      label: null,
      id: `${product.ean}-suggested-product-color-select`
    };
    const brand = localizeString(product.extraName, currentLanguage);
    const name = `${product.manufacturer} ${cutLongName(localizeString(product.name, currentLanguage))}`;

    return {
      key: `product-card-${product.ean}`,
      url: product?.url?.includes(Config.teliaShopUrl)
        ? `${product?.url}${urlParam ? `?search=${urlParam}` : ''}`
        : `${Config.teliaShopUrl}${product?.url}${urlParam ? `?search=${urlParam}` : ''}`,
      title: { brand, name },
      image: {
        src: this.imageUri,
        role: 'presentation',
        onError: this.setToDefaultImage
      },
      inactive: inactiveStatuses.includes(product.status),
      inactiveContent: inactiveStatuses.includes(product.status) ? translate(`search.productCard.${product.status}`) : null,
      colors: ssoStore.isDealer ? colors : resolveColors(product, currentLanguage, urlParam),
      onClick: () => {
        onClickCallback && onClickCallback(product);
      }
    };
  }

  private resolveCartButtonHelper(): IProductCardCartButtonHelper {
    if (this.cartStatus === FetchStatus.ERROR) {
      return {
        options: {
          type: 'error',
          text: this.context.translate('search.productCard.addToCartError')
        },
        visible: this.cartStatus === FetchStatus.ERROR
      };
    }
    return {
      options: {
        text: this.context.translate('search.productCard.addToCartSuccess')
      },
      visible: this.cartStatus === FetchStatus.FETCHED
    };
  }

  public render() {
    const translate = this.context.translate;
    const {
      product,
      languageStore: { currentLanguage },
      ssoStore,
      hasTelia1
    } = this.props;
    const customerType = ssoStore.customerType;
    const monthlyPriceList = product?.prices?.monthlyPrices || [];
    const currentPrice: Price = product?.prices?.currentPrice;
    const mobileLife = customerType === 'business' && product?.mobileLife;
    const monthlyPrice = getMonthlyPriceForCustomerType(monthlyPriceList, mobileLife, customerType);
    const previouslyBestPrice: Price = product?.prices?.previouslyBestPrice;
    const hasDiscount = previouslyBestPrice && +currentPrice.amount < +previouslyBestPrice.amount;
    const interestRate = monthlyPrice?.interestRate || (monthlyPrice?.financeType === 'MobileLife' && '0');
    const tagText = interestRate && `${formatInterestRate(interestRate)}% ${translate('productCard.interestTag')}`;
    const isVatEnabled = customerType !== 'business';
    const showPriceEndDates = currentPrice?.endDate && currentPrice?.durationVisible;
    const hasGift = product.tags?.some((tag) => tag.tagNames.et.toLowerCase().includes('kingitus'));
    const showAddToCartButton = ssoStore.isDealer && !inactiveStatuses.includes(product.status) && (currentPrice?.amount || monthlyPrice);

    return (
      <AnalyticsTrigger trigger={{ id: `Tab - ${ESHOP_CATEGORY} - Link - ${product.productCode}`, callback: 'onClick' }}>
        <ProductCard
          {...this.getBaseProps()}
          tags={mapTags(product, currentLanguage, product.prices)}
          headingLevel={3}
          previousPrice={
            hasDiscount && {
              label: product.serviceProduct ? translate('search.service.monthlyPrice') : addColon(localizeString(previouslyBestPrice.name, currentLanguage)),
              price: priceValue(
                isVatEnabled,
                getApplicablePrice(previouslyBestPrice.amount, previouslyBestPrice.amountWithoutTax, isVatEnabled),
                translate,
                previouslyBestPrice.currency
              ),
              ignoreCrossOut: !shouldCrossOutDiscountPrice(product.prices)
            }
          }
          price={
            currentPrice?.amount && {
              label: product.serviceProduct ? translate('search.service.monthlyPrice') : addColon(localizeString(currentPrice.name, currentLanguage)),
              price: priceValue(
                isVatEnabled,
                getApplicablePrice(currentPrice.amount, currentPrice.amountWithoutTax, isVatEnabled),
                translate,
                currentPrice.currency
              ),
              priceEndDate: showPriceEndDates && resolvePriceEndDate(currentPrice),
              priceEndDateLabel: showPriceEndDates && translate('pricePeriod.activeUntil')
            }
          }
          monthlyPrice={
            monthlyPrice &&
            product.status !== PRODUCT_STATUS_ARCHIVED && {
              label: addColon(getFinanceTypeTranslation(customerType, monthlyPrice.financeType as FinanceType, translate, false, interestRate, hasTelia1)),
              price: renderMonthlyPrice(
                isVatEnabled,
                getApplicablePrice(monthlyPrice.amount, monthlyPrice.amountWithoutTax, isVatEnabled),
                translate,
                monthlyPrice.currency
              ),
              length: `${monthlyPrice.period.toString()} ${translate('search.productCard.pricePeriod.months')}`,
              tagText
            }
          }
          cartButtonHelper={this.resolveCartButtonHelper()}
          cartButton={
            showAddToCartButton && {
              type: 'employee',
              iconLeft: hasGift ? undefined : '#cart',
              iconRight: hasGift ? '#arrow-right' : undefined,
              text: translate(hasGift ? 'search.productCard.viewMore' : 'search.productCard.addToCart'),
              onClick: !hasGift && this.handleAddToCart,
              url: hasGift ? this.getSelectedProductUrl() : undefined,
              isLoading: this.cartStatus === FetchStatus.FETCHING
            }
          }
        />
      </AnalyticsTrigger>
    );
  }
}

const getColor = (product: AlternativeProduct) => {
  const color = product.colorCode;
  return color ? handleGradient(color) : null;
};

const handleGradient = (color: string) => {
  const gradientColors = color.split(',');
  if (gradientColors.length === 2) {
    return `linear-gradient(to bottom right, ${gradientColors[0]} 50%, ${gradientColors[1]} 50%)`;
  }
  return color;
};

const resolveColors = (product: Product, currentLanguage: Language, searchParam?: string): IProductCardColor[] => {
  let colors: IProductCardColor[] = [];
  if (product.alternativeProducts?.length) {
    colors = colors.concat(
      product.alternativeProducts.map((altProduct) => {
        const url = getAlternativeProductUrl(altProduct.url);
        return {
          color: getColor(altProduct),
          label: capitalize(localizeString(altProduct.color, currentLanguage)),
          image: { srcset: getAlternativeProductImage(altProduct), role: 'presentation' },
          isCurrent: altProduct.productCode === product.productCode,
          url: url ? `${url}${searchParam ? `?search=${searchParam}` : ''}` : undefined
        };
      })
    );
  }
  colors = colors.filter((color) => color.color !== null);
  return colors.length > 1 ? colors : null;
};

const resolveColorSelectItems = (product: Product, currentLanguage: Language): IProductCardColorSelectOption[] => {
  const alternateProductsWithColours = product.alternativeProducts?.filter((altProduct) => !!getColor(altProduct));

  if (!alternateProductsWithColours || alternateProductsWithColours.length < 2) {
    return [];
  }
  return alternateProductsWithColours
    .filter((p) => !!p.url)
    .map((altProduct) => ({
      color: getColor(altProduct),
      id: `selector-${altProduct.productCode}`,
      label: localizeString(altProduct.color, currentLanguage),
      value: altProduct.productCode
    }));
};

const getAlternativeProductUrl = (url: string) => {
  return url ? Config.teliaShopUrl + url : null;
};

const getAlternativeProductImage = (alternativeProduct: AlternativeProduct) => {
  return alternativeProduct.picture && getImageSrcset(['200', '400'], alternativeProduct.picture);
};

const getMonthlyPriceForCustomerType = (monthlyPrices: MonthlyPrice[], mobileLife: MonthlyPrice, customerType: string): MonthlyPrice => {
  if (mobileLife) {
    return mobileLife;
  }
  if (customerType === CUSTOMER_TYPE_BUSINESS) {
    return monthlyPrices.filter((monthlyPrice) => monthlyPrice?.financeType === 'OperatingLease')[0];
  }
  return monthlyPrices.filter((monthlyPrice) => monthlyPrice?.financeType === 'Lease')[0];
};

const formatInterestRate = (price: string): string => {
  const number = Number.parseFloat(price).toFixed(1);
  if (number.endsWith('.0')) {
    return Number.parseFloat(price).toFixed(0);
  } else if (number.endsWith('0') && number.length) {
    return formatInterestRate(number.substr(0, number.length - 2));
  }
  return number;
};

export default inject('languageStore', 'ssoStore', 'shoppingCartStore')(ProductCardWrapper);
